import { useEffect, useState } from 'react';
import { X, ArrowRight, Users } from 'lucide-react';
import type { MesaVista } from '@shared/types';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { SpinnerInline } from '@renderer/components/ui/SpinnerInline';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  abierto: boolean;
  pedidoId: number | null;
  mesaActualId: number | null;
  onClose: () => void;
  onMovido: () => void;
}

/**
 * Modal para cambiar la mesa de un pedido en curso (Sprint 5 RN-S5-04).
 * Sólo permite mover a mesas en estado 'disponible' (excluye la actual).
 */
export function MoverMesaModal({ abierto, pedidoId, mesaActualId, onClose, onMovido }: Props) {
  const [mesas, setMesas] = useState<MesaVista[]>([]);
  const [seleccionada, setSeleccionada] = useState<number | null>(null);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sesion = useSessionStore((s) => s.sesion);

  useEffect(() => {
    if (!abierto) {
      setMesas([]);
      setSeleccionada(null);
      setError(null);
      return;
    }
    setCargando(true);
    window.api.mesas.listar().then((res) => {
      if (res.ok) setMesas(res.data);
      setCargando(false);
    });
  }, [abierto]);

  if (!abierto) return null;

  const disponibles = mesas.filter((m) => m.estado === 'disponible' && m.id !== mesaActualId);
  const mesaActual = mesas.find((m) => m.id === mesaActualId);

  const handleMover = async () => {
    if (!pedidoId || !seleccionada || !sesion) return;
    setEnviando(true);
    setError(null);
    const res = await window.api.pedidos.moverMesa({
      pedidoId,
      nuevaMesaId: seleccionada,
      usuarioId: sesion.usuarioId,
    });
    setEnviando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    onMovido();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold">Mover pedido a otra mesa</h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar" disabled={enviando}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-3">
          {mesaActual && (
            <div className="flex items-center justify-center gap-3 rounded-lg bg-slate-50 px-4 py-3 text-sm">
              <span className="font-medium">Mesa actual: #{mesaActual.numero}</span>
              <ArrowRight className="h-4 w-4 text-slate-400" />
              <span className="text-slate-500">Selecciona destino</span>
            </div>
          )}

          {cargando ? (
            <SpinnerInline texto="Cargando mesas…" className="py-8" />
          ) : disponibles.length === 0 ? (
            <div className="text-center text-slate-400 py-8 text-sm">
              No hay mesas disponibles para mover el pedido.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {disponibles.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSeleccionada(m.id)}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-3 transition ${
                    seleccionada === m.id
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                  }`}
                  disabled={enviando}
                >
                  <span className="text-2xl font-bold">#{m.numero}</span>
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Users className="h-3 w-3" /> {m.capacidad}
                  </span>
                </button>
              ))}
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700" role="alert">
              {error}
            </div>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1" disabled={enviando}>
            Cancelar
          </button>
          <button
            onClick={handleMover}
            className="btn-primary flex-1"
            disabled={!seleccionada || enviando}
          >
            {enviando ? 'Moviendo...' : 'Mover pedido'}
          </button>
        </footer>
      </div>
    </div>
  );
}
